import { View, SxProp, Image, useDripsyTheme, Pressable, FlatList, ActivityIndicator } from 'dripsy';
import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { useAsyncStorage } from '@/devtools/hooks/useAsyncStorage';
import { formatAmountByDecimal } from '@/features/account-management/utils';
import { useNavigation } from '@react-navigation/native';
import { PageItem } from '@/features/earn/components/page/PageItem';
import { H5, H6, TextBody, TextCaption, TextSmall, TextTitle } from '@/design-system/components/typography';
import { Row } from '@/design-system/components/layout';
import { UniIconButton } from '@/design-system/components/buttons';
import { Icon } from '@/design-system/components/icons/Icon';
import {
  TransactionStatusFilters,
  CardStatusFilters,
  CardStatusSelectFilters,
  CardNetworkFilters
} from '@/features/account-management/options';
import { TransactionStatus, CardStatus, CardType, CardAccountStatus } from '@/features/account-management/enum';
import { RootStackRoutes } from 'types';
import { AccountManagementStackRoutes } from '@/features/account-management/account.navigator';
import { LayoutContext } from 'contexts/LayoutContext';
import { PageContainerNative } from '@/features/account-management/screens/page/PageContainerNative';
import { useListNative } from '@/features/account-management/screens/details/hooks/useListNative.hook';
import { useTranslation } from 'react-i18next';
import { keyGenerator } from '@/utils/keyGenerator';
import { CardItem, phoneNumberProp, useCard, useCardTransaction } from '@/features/account-management/screens/details/hooks/useCard.hook';
import { CardAccountType } from '@/features/card-account/enum';
import { ListRenderItemInfo } from 'react-native';
import { CardAccountTransaction, Xmetadata } from '@utx/utx-access-client';
import { isEqual, uniq, uniqBy, uniqWith, capitalize } from 'lodash';
import { useHasFeature } from '@/design-system/components/hasFeature/useHasFeature.hook';
import { useAccountCreditSummary } from '@/features/card-account/hooks/useCardAccounts.hook';
import { useCardKycInfo } from '@/features/account-management/screens/details/hooks/useCardKycInfo.hook';

export interface CardDetailsProps {
  account: string;
  tab?: number;
  record: any;
  flag?: string;
  options?: { [key: string]: any };
}
const BANNER_HEIGHT_RATIO = 0.631034;

const NoData = () => {
  const { t } = useTranslation('earn');
  return (
    <View sx={styles.noData}>
      <Icon width={60} height={60} name="listZeroState" />
      <TextBody semiBold>{t('earn-invest::glossary.search-no-result')}</TextBody>
    </View>
  );
};

export const CardDetails: React.FC<CardDetailsProps> = (props) => {
  const { account, tab, record, flag, options } = props;
  const navigation = useNavigation();
  const { theme } = useDripsyTheme();
  const { t } = useTranslation('account-management');
  const { value: show, set: setShow } = useAsyncStorage<boolean>('showAmount');
  const [showCard, setShowCard] = useState<boolean>(true);
  const [showLimitCard, setShowLimitCard] = useState<boolean>(true);

  const { clientIdentity, isLoading, reload } = useCardKycInfo({
    cardAccountId: account
  });

  const showChange = (value: boolean) => {
    setShow(value);
  };
  const showChangeCard = (value: boolean) => {
    setShowCard(value);
  };
  const showChangeLimitCard = (value: boolean) => {
    setShowLimitCard(value);
  };
  const [showInfo, setShowInfo] = useState<boolean>(true);
  const showChangeInfo = (value: boolean) => {
    setShowInfo(value);
  };
  const layout = useContext(LayoutContext);
  const containerWidth = layout.dimensions.width - 78;

  const getColor = (status?: string) => {
    switch (status) {
      case TransactionStatus.PENDING:
        return '#28ACFF';
      case TransactionStatus.SUCCESS:
        return theme?.colors?.$success as string;
      case TransactionStatus.DECLINED:
        return theme?.colors?.$danger as string;
      default:
        return theme?.colors?.$textSecondary as string;
    }
  };
  const isNegative = (value: string) => {
    return (value && value.toString().startsWith('-')) || false;
  };
  const hideShowBalance = (balance: string) => {
    return show ? '******' : balance;
  };
  const upperFirstCharacter = (value: string) => {
    if (value.length === 0) return value;
    return value[0].toUpperCase() + value.substring(1);
  };

  const transactionHistory = () => {
    navigation.navigate(RootStackRoutes.AccountManagement, {
      screen: AccountManagementStackRoutes.CardTransactionHistory,
      params: { account }
    });
  };

  const { bottomSheetFilterProps, filter } = useListNative({
    status: CardStatusSelectFilters(t)
  });
  const [status, setStatus] = useState<string | undefined>(undefined);
  useEffect(() => {
    clearCardCurrentPageToken();
    let statusFlag = filter?.status ? filter?.status[0] : 'ALL';
    setStatus(statusFlag == 'ALL' ? undefined : statusFlag);
  }, [filter]);

  const getCardColor = (status?: string) => {
    switch (status) {
      case CardStatus.ACTIVE:
        return theme?.colors?.$success as string;
      case CardStatus.PENDING_ACTIVATION:
        return '#0EBBBE';
      case CardStatus.LOCKED:
        return '#DABE18';
      case CardStatus.SUSPENDED:
        return theme?.colors?.$tagRedBackground as string;
      case CardStatus.CANCELLED:
        return theme?.colors?.$tagGreyBackground as string;
      default:
        return theme?.colors?.$textSecondary as string;
    }
  };

  const [searchId, setSearchId] = useState<string | undefined>(undefined);
  const {
    cards,
    isLoadingCards,
    reloadCards,
    nextTokenCard,
    preTokenCard,
    prevCard,
    nextCard,
    lockCard,
    clearCardCurrentPageToken,
    unlockCard,
    changeCardPin,
    setCardLimit,
    actionResponse,
    actionError,
    cardAccounts,
    isLoadingCardAccounts,
    reloadCardAccounts,
    topUpCardAccount,
    withdrawCardAccount,
    issueCard,
    activateCard,
    cancelCard,
    suspendCard,
    unsuspendCard,
    updateCardPhoneNumberCard,
    postCardaccountsFixedLimit
  } = useCard({ cardAccountId: account, status: status, cardId: searchId });
  const { transactions, isLoadingCardTransactions, reloadCardTransactions, nextToken, preToken, next, prev, refreshCardTransactions } =
    useCardTransaction({
      cardAccountId: account,
      cardId: searchId
    });

  const funcLockCard = (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    lockCard(cardId, onSuccess, onError);
  };

  const funcUnlockCard = (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    unlockCard(cardId, onSuccess, onError);
  };

  const funcChangeCardPin = (cardId: string, pin: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    changeCardPin(cardId, pin, onSuccess, onError);
  };

  const funcSetCardLimit = (
    cardId: string,
    atmLimit?: number,
    purchaseLimit?: number,
    dailyLimit?: number,
    singleTransactionLimit?: number,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    setCardLimit(cardId, atmLimit, purchaseLimit, dailyLimit, singleTransactionLimit, onSuccess, onError);
  };

  const funcTopUpCardAccount = (
    accountId: string,
    amount: number,
    currency: string,
    xmetadata: Xmetadata,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    topUpCardAccount(accountId, amount, currency, xmetadata, onSuccess, onError);
  };

  const funcWithdrawCardAccount = (
    accountId: string,
    amount: number,
    currency: string,
    xmetadata: Xmetadata,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    withdrawCardAccount(accountId, amount, currency, xmetadata, onSuccess, onError);
  };

  const funcIssueCard = (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    issueCard(data, onSuccess, onError);
  };

  const funcActivateCard = (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    activateCard(cardId, onSuccess, onError);
  };

  const funcCancelCard = (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    cancelCard(data, onSuccess, onError);
  };

  const funcSuspendCard = (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    suspendCard(cardId, onSuccess, onError);
  };

  const funcUnsuspendCard = (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    unsuspendCard(cardId, onSuccess, onError);
  };

  const funcUpdateCardPhoneNumberCard = (
    cardId: string,
    phoneNumber: phoneNumberProp,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    updateCardPhoneNumberCard(cardId, phoneNumber, onSuccess, onError);
  };

  const funcPostCardaccountsFixedLimit = (limit: number, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    postCardaccountsFixedLimit(limit, onSuccess, onError);
  };

  const [cardAccountsOverview, setCardAccountsOverview] = useState<{
    balance: number;
    limit: number;
    available: number;
    outstanding: number;
    currency: string;
    name: string | undefined;
    accountType: string | undefined;
    walletId?: string;
    status?: string;
    settlementAccountName?: string;
    settlementAccountNumber?: string;
  }>({ balance: 0, limit: 0, available: 0, outstanding: 0, currency: 'HKD', name: '', accountType: '', walletId: undefined, status: '', settlementAccountName: '', settlementAccountNumber: '' });

  const { creditSummary, isLoadingCreditSummary } = useAccountCreditSummary({
    walletId: cardAccountsOverview.walletId,
    currency: cardAccountsOverview.currency,
    accountType: cardAccountsOverview.accountType
  });

  useEffect(() => {
    let balanceAmount = 0;
    let limitAmount = 0;
    let availableAmount = 0;
    let outstandingAmount = 0;
    let currencyAmount = 'HKD';
    let cardAccountName = undefined;
    let cardAccountType = undefined;
    let walletId = undefined;
    let status = undefined;
    let settlementAccountName = undefined;
    let settlementAccountNumber = undefined;
    if (!isLoadingCardAccounts) {
      if (cardAccounts) {
        cardAccounts.forEach((cardAccount) => {
          balanceAmount += cardAccount?.balance;
          limitAmount += cardAccount?.limit;
          availableAmount += cardAccount?.available;
          outstandingAmount += cardAccount?.outstanding;
          currencyAmount = cardAccount?.currency;
          cardAccountName = cardAccount?.name;
          cardAccountType = cardAccount?.accountType;
          walletId = cardAccount?.walletId;
          status = cardAccount?.status;
          settlementAccountName = cardAccount?.settlementAccountName;
          settlementAccountNumber = cardAccount?.settlementAccountNumber;
        });
        setCardAccountsOverview({
          balance: balanceAmount,
          limit: limitAmount,
          available: availableAmount,
          outstanding: outstandingAmount,
          currency: currencyAmount,
          name: cardAccountName,
          accountType: cardAccountType,
          walletId: walletId,
          status: status,
          settlementAccountName: settlementAccountName,
          settlementAccountNumber: settlementAccountNumber
        });
      } else {
        setCardAccountsOverview({
          balance: balanceAmount,
          limit: limitAmount,
          available: availableAmount,
          outstanding: outstandingAmount,
          currency: currencyAmount,
          name: cardAccountName,
          accountType: cardAccountType,
          walletId: undefined,
          status: status
        });
      }
    }
  }, [isLoadingCardAccounts, cardAccounts]);

  const [cardsData, setCardsData] = useState<CardItem[]>([]);
  useEffect(() => {
    let cardsFlag = uniqBy([...cards, ...cardsData], 'id');
    setCardsData(cardsFlag);
  }, [cards]);

  useEffect(() => {
    if (options?.card && cards.length > 0) {
      const card = cards.find((item) => item.id === options.card.id);
      if (card && card.status === CardStatus.PENDING_ACTIVATION) {
        setTimeout(() => {
          navigation.navigate(RootStackRoutes.AccountManagement, {
            screen: AccountManagementStackRoutes.CardDetail,
            params: {
              record: { ...card, currency: cardAccountsOverview.currency, limit: cardAccountsOverview.limit },
              account,
              tab,
              funcLockCard,
              funcUnlockCard,
              funcSetCardLimit,
              funcChangeCardPin,
              refresh: reloadCards,
              funcActivateCard
            }
          });
        }, 500);
      }
    }
  }, [cards]);

  useEffect(() => {
    if (options?.card) {
      setSearchId(options.card.id);
    }
  }, [options]);

  const isEnabled = useHasFeature({ feature: 'ACCESS_CARD_BALANCE_WITHDRAW', variation: 'enabled' })?.enabled;

  return (
    <View sx={styles.contentContainer}>
      <PageContainerNative>
        <Row sx={{ justifyContent: 'space-between', alignItems: 'center', px: '$4', mt: 10, mb: 10 }}>
          <UniIconButton
            icon="arrowLeft"
            options={{ iconStyles: { color: theme?.colors?.$white as string } }}
            onPress={() => navigation.getParent()?.goBack()}></UniIconButton>
          <View sx={{ textAlign: 'center' }}>
            <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
              {(flag == '1' ? record?.label : record?.name) || '-'}
            </TextTitle>
            <TextSmall sx={{ mt: '$1', color: theme?.colors?.$white as string }}>
              {(flag == '1' ? (
                <View sx={{ gap: '$2', flexDirection: 'row' }}>
                  <TextCaption sx={{ color: theme?.colors?.$white as string }}>{record.assetSymbol}</TextCaption>
                  <TextCaption sx={{ color: theme?.colors?.$white as string }}>{upperFirstCharacter(record.type)}</TextCaption>
                  <TextCaption sx={{ color: theme?.colors?.$white as string }}>{capitalize(cardAccountsOverview.status)}</TextCaption>
                </View>
              ) : (
                record?.accountNumber
              )) || '-'}
            </TextSmall>
          </View>
          <UniIconButton
            icon="closeCircle"
            options={{ iconStyles: { color: theme?.colors?.$white as string, width: 24, height: 24 } }}
            onPress={() => navigation.getParent()?.goBack()}></UniIconButton>
        </Row>

        <PageItem sx={styles.summaryCard}>
          <View sx={styles.summaryHeader}>
            <TextTitle semiBold>{t('card-account-management::card-details.title--credit-summary')}</TextTitle>
            <View sx={{ flexDirection: 'row', gap: '$3' }}>
              <View sx={{ flexDirection: 'row', gap: '$3' }}>
                <UniIconButton
                  icon="info"
                  options={{ iconStyles: { width: 24, height: 24 } }}
                  onPress={() => {
                    navigation.navigate(RootStackRoutes.AccountManagement, {
                      screen: AccountManagementStackRoutes.UpdateKycInfo,
                        params: {
                          data: clientIdentity,
                          refresh: reload,
                        }
                      })
                  }}
                />
              </View>
            {flag == '1' &&
              !isLoadingCardAccounts &&
              cardAccountsOverview.accountType &&
              cardAccountsOverview.accountType == CardAccountType.PREPAID && (
                <View sx={{ flexDirection: 'row', gap: '$3' }}>
                  <UniIconButton
                    icon="topUp"
                    options={{ iconStyles: { width: 24, height: 24 } }}
                    onPress={() => {
                      let item = { currency: cardAccountsOverview?.currency, name: cardAccountsOverview?.name };
                      navigation.navigate(RootStackRoutes.AccountManagement, {
                        screen: AccountManagementStackRoutes.TopUp,
                        params: {
                          record: item,
                          func: funcTopUpCardAccount,
                          refresh: () => {
                            reloadCardAccounts();
                            refreshCardTransactions();
                          }
                        }
                      });
                    }}
                  />
                  {isEnabled && (
                    <UniIconButton
                      icon="withdrawMobile"
                      options={{ iconStyles: { width: 24, height: 24 } }}
                      onPress={() => {
                        let item = {
                          currency: cardAccountsOverview?.currency,
                          name: cardAccountsOverview?.name,
                          available: cardAccountsOverview?.available
                        };
                        navigation.navigate(RootStackRoutes.AccountManagement, {
                          screen: AccountManagementStackRoutes.Withdraw,
                          params: {
                            record: item,
                            func: funcWithdrawCardAccount,
                            refresh: () => {
                              reloadCardAccounts();
                              refreshCardTransactions();
                            }
                          }
                        });
                      }}
                    />
                  )}
                </View>
              )}
            {flag == '1' &&
              !isLoadingCardAccounts &&
              !isLoadingCreditSummary &&
              cardAccountsOverview.accountType &&
              cardAccountsOverview.accountType == CardAccountType.FIXED_LIMIT && (
                <View sx={{ flexDirection: 'row', gap: '$3' }}>
                  <UniIconButton
                    icon="limit"
                    options={{ iconStyles: { width: 30, height: 24 } }}
                    onPress={() => {
                      let item = { currency: cardAccountsOverview?.currency, name: cardAccountsOverview?.name, creditSummary: creditSummary };
                      navigation.navigate(RootStackRoutes.AccountManagement, {
                        screen: AccountManagementStackRoutes.FixedLimit,
                        params: {
                          record: item,
                          func: funcPostCardaccountsFixedLimit,
                          refresh: () => {
                            reloadCardAccounts();
                          }
                        }
                      });
                    }}
                  />
                </View>
              )}
            </View>
          </View>
          <View sx={styles.summaryContent}>
            <View sx={{ width: '100%', gap: '$1' }}>
              <Row sx={{ alignItems: 'center', gap: '$1' }}>
                <TextBody semiBold secondary>
                  {t('card-account-management::account-page.text--card-account-balance')}
                </TextBody>
                <UniIconButton icon={show ? 'hide' : 'show'} onPress={(e) => showChange(!show)} />
              </Row>
              <Row sx={{ mt: '$1', mb: '$3', alignItems: 'center' }}>
                <H5
                  bold
                  sx={{
                    color: (cardAccountsOverview?.balance &&
                    cardAccountsOverview?.balance > 0 &&
                    cardAccountsOverview.accountType == CardAccountType.PREPAID
                      ? theme?.colors?.$tagRedBackground
                      : theme?.colors?.$secondary) as string
                  }}>
                  {hideShowBalance(
                    formatAmountByDecimal({
                      value: Math.abs(cardAccountsOverview?.balance),
                      suffix: `${cardAccountsOverview?.currency} ${isNegative(cardAccountsOverview?.balance.toString()) ? '[CR]' : ''}`,
                      decimals: 2
                    })
                  )}
                </H5>
              </Row>
            </View>
          </View>
          <View sx={{ gap: '$2', my: '$5' }}>
            <View sx={{ flexDirection: 'row', justifyContent: 'space-between', gap: '$2' }}>
              <TextBody sx={{ color: '$textSecondary' }}>{t('card-account-management::glossary.outsanding-balance')}</TextBody>
              <TextBody semiBold sx={{ color: '$textPrimary' }}>
                {hideShowBalance(
                  formatAmountByDecimal({ value: cardAccountsOverview.outstanding, suffix: cardAccountsOverview.currency, decimals: 2 })
                )}
              </TextBody>
            </View>
            {cardAccountsOverview.accountType && cardAccountsOverview.accountType != CardAccountType.PREPAID && (
              <View sx={{ flexDirection: 'row', justifyContent: 'space-between', gap: '$2' }}>
                <TextBody sx={{ color: '$textSecondary' }}>{t('card-account-management::card-details.text--available-limit')}</TextBody>
                <TextBody semiBold sx={{ color: '$textPrimary' }}>
                  {hideShowBalance(
                    formatAmountByDecimal({ value: cardAccountsOverview.available, suffix: cardAccountsOverview.currency, decimals: 2 })
                  )}
                </TextBody>
              </View>
            )}
            {cardAccountsOverview.accountType && cardAccountsOverview.accountType != CardAccountType.PREPAID && (
              <View sx={{ flexDirection: 'row', justifyContent: 'space-between', gap: '$2' }}>
                <TextBody sx={{ color: '$textSecondary' }}>{t('card-account-management::card-details.text--card-account-limit')}</TextBody>
                <TextBody semiBold sx={{ color: '$textPrimary' }}>
                  {hideShowBalance(
                    formatAmountByDecimal({ value: cardAccountsOverview.limit, suffix: cardAccountsOverview.currency, decimals: 2 })
                  )}
                </TextBody>
              </View>
            )}
            {cardAccountsOverview.accountType && cardAccountsOverview.accountType == CardAccountType.FIXED_LIMIT && (
              <View sx={{ gap: '$2' }}>
                <View sx={{ flexDirection: 'row', justifyContent: 'space-between', gap: '$2' }}>
                  <TextBody sx={{ color: '$textSecondary' }}>{t('card-account-management::card-details.text--settlement-account-name-native')}</TextBody>
                  <TextBody semiBold sx={{ color: '$textPrimary' }}>
                    <TextBody semiBold>{cardAccountsOverview?.settlementAccountName}</TextBody>
                  </TextBody>
                </View>
                <View sx={{ flexDirection: 'row', justifyContent: 'space-between', gap: '$2' }}>
                  <TextBody sx={{ color: '$textSecondary' }}>{t('card-account-management::card-details.text--settlement-account-number-native')}</TextBody>
                  <TextBody semiBold sx={{ color: '$textPrimary' }}>
                    <TextBody semiBold>{cardAccountsOverview?.settlementAccountNumber}</TextBody>
                  </TextBody>
                </View>
              </View>
            )}
          </View>
        </PageItem>
      </PageContainerNative>
      <View sx={styles.overviewCard}>
        <PageItem sx={styles.CardOverview}>
          <View sx={styles.overviewDetails}>
            <View sx={{ flex: 1, flexDirection: 'column', gap: '$10' }}>
              <View>
                <Row sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                  <TextTitle bold>{t('general::cards')}</TextTitle>
                  { cardAccountsOverview.status != CardAccountStatus.FROZEN &&  
                    <UniIconButton
                      icon="topUp"
                      options={{ iconStyles: { width: 24, height: 24 } }}
                      onPress={() => {
                        navigation.navigate(RootStackRoutes.AccountManagement, {
                          screen: AccountManagementStackRoutes.IssueCard,
                          params: {
                            account: account,
                            tab: tab,
                            record: record,
                            func: funcIssueCard,
                            refresh: reloadCards
                          }
                        });
                      }}
                    />
                  }
                </Row>
                <FlatList
                  data={cardsData}
                  renderItem={({ item }: ListRenderItemInfo<CardItem>) => {
                    return (
                      <Pressable
                        onPress={() => {
                          navigation.navigate(RootStackRoutes.AccountManagement, {
                            screen: AccountManagementStackRoutes.CardDetail,
                            params: {
                              record: { ...item,
                                currency: cardAccountsOverview.currency,
                                limit: cardAccountsOverview.limit,
                                cardAccountStatus: cardAccountsOverview.status },
                              account,
                              tab,
                              funcLockCard,
                              funcUnlockCard,
                              funcSetCardLimit,
                              funcChangeCardPin,
                              refresh: reloadCards,
                              funcActivateCard,
                              funcCancelCard,
                              funcSuspendCard,
                              funcUnsuspendCard,
                              funcUpdateCardPhoneNumberCard
                            }
                          });
                        }}
                        sx={{ py: '$2', marginTop: '$5', marginRight: '$4', marginLeft: 1 }}>
                        <View sx={{ padding: '$4', gap: '$5', borderRadius: '$radius2', boxShadow: '2px 4px 20px rgba(0, 0, 0, 0.07)' }}>
                          <View sx={{ gap: '$3' }}>
                            <View sx={{ gap: '$3', flexDirection: 'row', justifyContent: 'space-between' }}>
                              <Image
                                sx={{ width: 50, height: 30 }}
                                source={item?.product?.imageUrl || require('assets/images/worldcard_blank.png')}></Image>
                              <View sx={{ flexDirection: 'row', alignItems: 'center', paddingLeft: '$8' }}>
                                <Icon name="dotsymbol" color={getCardColor(item.status)} />
                                <TextCaption sx={{ color: getCardColor(item.status) }}>
                                  {CardStatusFilters(t).find((el) => el.value === item.status)?.label}
                                </TextCaption>
                              </View>
                            </View>
                            <View sx={{ justifyContent: 'center', alignItems: 'flex-start', gap: '$1' }}>
                              <TextTitle semiBold sx={{ color: '$textPrimary' }}>
                                {item.embossedName}
                              </TextTitle>
                              <TextCaption sx={{ color: '$textPrimary' }}>
                                {CardNetworkFilters(t).find((rec) => rec.value === item?.network)?.label} {`**** ${item.last4}`}
                              </TextCaption>
                            </View>
                          </View>
                          <View sx={{ gap: '$1' }}>
                            <TextCaption semiBold sx={{ color: '$textSecondary' }}>
                              {t('card-account-management::card-details.text--todays-spending')}
                            </TextCaption>
                            <H6 bold sx={{ color: theme?.colors?.$secondary as string }}>
                              {hideShowBalance(
                                formatAmountByDecimal({
                                  value: item?.cardSpendingToday || 0,
                                  suffix: cardAccountsOverview.currency,
                                  decimals: 2
                                })
                              )}
                            </H6>
                          </View>
                        </View>
                      </Pressable>
                    );
                  }}
                  sx={{ minWidth: '75%' }}
                  horizontal
                  scrollEnabled={true}
                  showsVerticalScrollIndicator={false}
                  keyExtractor={(_, index) => keyGenerator(`card-${index}`)}
                  onEndReachedThreshold={0.05}
                  onEndReached={() => {
                    if (!isLoadingCards) {
                      if (nextTokenCard) nextCard();
                    }
                  }}
                />
                {isLoadingCards && (
                  <View sx={{ padding: '$5' }}>
                    <ActivityIndicator size="large" color={(theme.colors?.$disable as string) || '#F0F0F0'} />
                  </View>
                )}
                {!isLoadingCards && cardsData.length === 0 && <NoData />}
              </View>
            </View>
          </View>
          <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mt: '$5' }}>
            <TextTitle bold>{t('card-account-management::account-page.title--transaction-history')}</TextTitle>
            {transactions.length > 0 && <UniIconButton icon="arrowRight" onPress={transactionHistory}></UniIconButton>}
          </View>
          <View sx={styles.overviewTransaction}>
            <View sx={styles.transactionContent}>
              <FlatList
                data={transactions.slice(0, 3)}
                renderItem={({ item }: ListRenderItemInfo<CardAccountTransaction>) => {
                  return (
                    <Pressable onPress={() => {}} sx={{ py: '$3' }}>
                      <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <View sx={{ flexDirection: 'row', alignItems: 'center' }}>
                          <TextBody semiBold sx={{ color: '$textPrimary' }}>
                            {item.description}
                          </TextBody>
                        </View>
                        <View sx={{ flexDirection: 'row', alignItems: 'center' }}>
                          <TextBody
                            semiBold
                            sx={{
                              color: (item?.status == 'pending' ? theme?.colors?.$navbarTextInactive : theme?.colors?.$secondary) as string
                            }}>
                            {hideShowBalance(
                              formatAmountByDecimal({
                                value: `${item.amount}`,
                                suffix: `${item?.currency} ${item?.entryType == 'CREDIT' ? '[CR]' : ''}`,
                                decimals: 2
                              })
                            )}
                          </TextBody>
                        </View>
                      </View>
                      <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mt: '$2' }}>
                        <View sx={{ flexDirection: 'row', alignItems: 'center' }}>
                          <TextCaption sx={{ color: theme?.colors?.$textSecondary as string }}>
                            {item.cardEmbossedName} {item.cardLast4 ? `**** ${item.cardLast4}` : '-'}
                          </TextCaption>
                        </View>
                        <View sx={{ flexDirection: 'row', alignItems: 'center' }}>
                          <Icon name="dotsymbol" color={getColor(item.status)} />
                          <TextCaption sx={{ color: getColor(item.status) }}>
                            {TransactionStatusFilters(t).find((el) => el.value === item.status)?.label}
                          </TextCaption>
                        </View>
                      </View>
                      <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mt: '$2' }}>
                        <View sx={{ flexDirection: 'row', alignItems: 'center' }}>
                          <Icon name="clock1" width={8} height={8} />
                          <TextSmall sx={{ ml: '$2', color: '$textPlaceholder' }}>
                            {item.createdAt ? moment(item.createdAt).format('YYYY-MM-DD') : '-'}
                          </TextSmall>
                        </View>
                      </View>
                    </Pressable>
                  );
                }}
              />
              {isLoadingCardTransactions && (
                <View sx={{ padding: '$5' }}>
                  <ActivityIndicator size="large" color={(theme.colors?.$disable as string) || '#F0F0F0'} />
                </View>
              )}
              {!isLoadingCardTransactions && transactions.length === 0 && <NoData />}
            </View>
          </View>
        </PageItem>
      </View>
    </View>
  );
};

enum StyleName {
  contentContainer = 'contentContainer',
  card = 'card',
  CardOverview = 'CardOverview',
  summaryCard = 'summaryCard',
  transactionCard = 'transactionCard',
  summaryHeader = 'summaryHeader',
  summaryContent = 'summaryContent',
  overviewCard = 'overviewCard',
  overviewHeader = 'overviewHeader',
  overviewDetails = 'overviewDetails',
  overviewTransaction = 'overviewTransaction',
  transactionHeader = 'transactionHeader',
  transactionContent = 'transactionContent',
  transactionFooter = 'transactionFooter',
  cardButton = 'cardButton',
  cardButtonContainer = 'cardButtonContainer',
  cardActionBtn = 'cardActionBtn',
  carouselTextGroup = 'carouselTextGroup',
  filters = 'filters',
  filter_search = 'filter_search',
  productFilterNativeIconBtn = 'productFilterNativeIconBtn',
  productFilterIconNumber = 'productFilterIconNumber',
  infoContainer = 'infoContainer',
  showInfo = 'showInfo',
  noData = 'noData'
}

const styles: Record<StyleName, SxProp> = {
  contentContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateAreas: `
      "summary summary"
      "overview overview"
    `
  },
  CardOverview: {
    borderRadius: 0,
    flexDirection: 'column',
    marginY: '$1',
    pt: '$5',
    px: '$4',
    pb: 0
  },
  card: { flexDirection: 'column', padding: '$4' },
  summaryCard: {
    gridArea: 'summary',
    px: '$4',
    paddingTop: '$4',
    paddingBottom: '$2',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0
  },
  transactionCard: {
    borderRadius: 0,
    paddingTop: '$5',
    paddingBottom: '$2'
    // marginBottom: 50
  },
  summaryHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', mb: '$10' },
  summaryContent: { flexDirection: 'row', alignItems: 'center' },
  overviewCard: { gridArea: 'overview' },
  overviewHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  overviewDetails: { flexDirection: 'row' },
  overviewTransaction: { flexDirection: 'column', gap: '$5' },
  transactionHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 },
  transactionContent: { mt: '$5' },
  transactionFooter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  cardButton: {
    width: 40,
    height: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 100,
    backgroundColor: '$filterSelected'
  },
  cardButtonContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: '$1',
    overflowX: 'auto',
    overflowY: 'hidden'
  },

  cardActionBtn: {
    width: 100,
    alignItems: 'center',
    textAlign: 'center',
    gap: '$1'
  },
  carouselTextGroup: {
    position: 'absolute',
    top: 0,
    left: '$2',
    paddingX: '$4',
    paddingTop: '$11',
    paddingBottom: '$5',
    gap: '$3',
    justifyContent: 'flex-end'
  },
  filters: {
    flexDirection: 'row',
    gap: '$5',
    width: '100%',
    paddingX: '$4',
    marginTop: '$4',
    marginBottom: '$2'
  },
  filter_search: {
    width: '100%'
  },
  productFilterNativeIconBtn: {
    height: '$9',
    width: '$9',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '$borderButton',
    borderRadius: 5
  },
  productFilterIconNumber: {
    position: 'absolute',
    right: 0,
    width: '$2',
    height: '$2',
    borderWidth: 1,
    borderRadius: 100,
    borderColor: '$hyperlink',
    backgroundColor: '$hyperlink',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100
  },
  infoContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
  showInfo: { flexDirection: 'row' },
  noData: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '$3',
    minHeight: 200
  }
};
